const SALUT_QA_MONTHS = [
  'ian',
  'feb',
  'mar',
  'apr',
  'mai',
  'iun',
  'iul',
  'aug',
  'sep',
  'oct',
  'noi',
  'dec',
];

const SALUT_QA_VISIBLE_BLOCKS = 2;

function salutQaEscape(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, (character) => {
    return {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[character];
  });
}

function salutQaParse(element) {
  if (!element) return null;
  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    return null;
  }
}

function salutQaDate(value) {
  const parts = String(value).split('-');
  if (parts.length !== 3) return '';
  const month = SALUT_QA_MONTHS[Number(parts[1]) - 1] || '';
  return `${Number(parts[2])} ${month} ${parts[0]}`;
}

function salutQaAnswer(question) {
  const answer = question && question.answer;
  if (!answer || typeof answer !== 'object') return null;
  return answer.text ? answer : null;
}

function salutQaAnswered(questions) {
  return questions.filter((question) => salutQaAnswer(question));
}

class SalutQa extends HTMLElement {
  connectedCallback() {
    this.labels = salutQaParse(this.querySelector('[data-salut-qa-labels]')) || {};
    this.perPage = Number(this.labels.per_page) || 3;
    this.state = { expanded: false, aiExpanded: false };

    this.list = this.querySelector('[data-list]');
    this.extra = this.querySelector('[data-extra]');
    this.extraList = this.querySelector('[data-extra-list]');
    this.moreButton = this.querySelector('[data-more]');
    this.countRoot = this.querySelector('[data-count]');
    this.aiBody = this.querySelector('[data-ai-body]');
    this.aiExtra = this.querySelector('[data-ai-extra]');
    this.aiExtraBody = this.querySelector('[data-ai-extra-body]');
    this.aiMore = this.querySelector('[data-ai-more]');
    this.aiCard = this.querySelector('.salut-qa__ai');
    this.dialog = this.querySelector('[data-dialog]');
    this.helpTemplate = this.querySelector('[data-icon-help]');
    this.chevronTemplate = this.querySelector('[data-icon-chevron-down]');
    this.closeTemplate = this.querySelector('[data-icon-close]');

    this.load().then((data) => {
      this.questions = salutQaAnswered(Array.isArray(data && data.questions) ? data.questions : []);
      this.overview = (data && data.overview) || {};
      this.renderOverview();
      this.bind();
      this.render();
    });
  }

  endpoints() {
    const raw = (this.dataset.endpoint || '').trim();
    if (!raw) return null;
    const [base, existingQuery] = raw.split('?');
    const clean = base.replace(/\/+$/, '');
    const params = new URLSearchParams(existingQuery || '');
    const product = this.dataset.product || '';
    if (product) params.set('product', product);
    const locale = (document.documentElement.lang || '').trim();
    if (locale) params.set('locale', locale);
    const query = params.toString();
    return { list: query ? `${clean}?${query}` : clean, submit: clean };
  }

  load() {
    const urls = this.endpoints();
    if (urls) {
      return fetch(urls.list)
        .then((response) => response.json())
        .catch(() => ({ questions: [], overview: {} }));
    }
    return Promise.resolve(salutQaParse(this.querySelector('[data-salut-qa]')) || {});
  }

  blockMarkup(block) {
    return `
      <div class="salut-qa__ai-block">
        <span class="salut-qa__ai-heading">${salutQaEscape(block.heading)}</span>
        <p class="salut-qa__ai-text">${salutQaEscape(block.text)}</p>
      </div>
    `;
  }

  renderOverview() {
    const blocks = Array.isArray(this.overview.blocks) ? this.overview.blocks : [];
    if (this.aiCard) this.aiCard.hidden = blocks.length === 0;
    if (this.aiBody) {
      this.aiBody.innerHTML = blocks.slice(0, SALUT_QA_VISIBLE_BLOCKS).map((block) => this.blockMarkup(block)).join('');
    }
    if (this.aiExtraBody) {
      this.aiExtraBody.innerHTML = blocks.slice(SALUT_QA_VISIBLE_BLOCKS).map((block) => this.blockMarkup(block)).join('');
    }
    if (this.aiMore) {
      this.aiMore.hidden = blocks.length <= SALUT_QA_VISIBLE_BLOCKS;
    }
    this.syncOverview();
  }

  syncOverview() {
    if (this.aiExtra) this.aiExtra.setAttribute('data-expanded', String(this.state.aiExpanded));
    if (!this.aiMore) return;
    this.aiMore.classList.toggle('salut-qa__ai-more--open', this.state.aiExpanded);
    this.aiMore.setAttribute('aria-expanded', String(this.state.aiExpanded));
    const label = this.aiMore.querySelector('[data-ai-more-label]');
    if (label) label.textContent = this.state.aiExpanded ? this.labels.ai_less : this.labels.ai_more;
  }

  visibleQuestions() {
    return this.questions.slice().sort((first, second) => {
      const helpful = (Number(second.helpful) || 0) - (Number(first.helpful) || 0);
      if (helpful) return helpful;
      return String(second.date).localeCompare(String(first.date));
    });
  }

  renderCount() {
    if (!this.countRoot) return;
    const count = this.questions.length;
    const word = count === 1 ? this.labels.count_one : this.labels.count_many;
    this.countRoot.textContent = `${count} ${word || ''}`.trim();
  }

  rowMarkup(question) {
    const icon = this.helpTemplate ? this.helpTemplate.innerHTML : '';
    const answer = salutQaAnswer(question);

    return `
      <article class="salut-qa__row">
        <div class="salut-qa__question">
          <span class="salut-qa__question-icon">${icon}</span>
          <h3 class="salut-qa__question-text">${salutQaEscape(question.question)}</h3>
        </div>
        <p class="salut-qa__answer">${salutQaEscape(answer.text)}</p>
        <span class="salut-qa__by">— ${salutQaEscape(answer.author)}${
          answer.role ? ` · ${salutQaEscape(answer.role)}` : ''
        }</span>
      </article>
    `;
  }

  render() {
    this.renderCount();
    const list = this.visibleQuestions();

    if (!list.length) {
      this.list.innerHTML = `<p class="salut-qa__empty">${salutQaEscape(this.labels.empty)}</p>`;
    } else {
      this.list.innerHTML = list
        .slice(0, this.perPage)
        .map((question) => this.rowMarkup(question))
        .join('');
    }

    if (this.extraList) {
      this.extraList.innerHTML = list
        .slice(this.perPage)
        .map((question) => this.rowMarkup(question))
        .join('');
    }
    if (this.extra) this.extra.setAttribute('data-expanded', String(this.state.expanded));

    if (this.moreButton) {
      this.moreButton.hidden = list.length <= this.perPage;
      this.moreButton.classList.toggle('salut-qa__more--open', this.state.expanded);
      this.moreButton.setAttribute('aria-expanded', String(this.state.expanded));
      const label = this.moreButton.querySelector('[data-more-label]');
      if (label) label.textContent = this.state.expanded ? this.labels.show_less : this.labels.show_all;
    }
  }

  bind() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-more]')) {
        this.state.expanded = !this.state.expanded;
        this.render();
        return;
      }

      if (event.target.closest('[data-ai-more]')) {
        this.state.aiExpanded = !this.state.aiExpanded;
        this.syncOverview();
        return;
      }

      if (event.target.closest('[data-ask]')) {
        this.openDialog(event.target.closest('[data-ask]'));
      }
    });
  }

  categories() {
    return String(this.labels.form_categories || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  openDialog(opener) {
    if (!this.dialog) return;
    this.form = { category: '', question: '', menu: false, submitted: false };
    this.dialogOpener = opener || null;
    this.renderDialog();
    this.dialog.showModal();
    document.body.classList.add('salut-qa-locked');
    const inner = this.dialog.querySelector('.salut-qa__form');
    if (inner) inner.focus();
    if (!this.dialogBound) this.bindDialog();
  }

  bindDialog() {
    this.dialogBound = true;

    this.dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-close]') || event.target === this.dialog) {
        this.dialog.close();
        return;
      }

      if (event.target.closest('[data-menu-toggle]')) {
        if (this.form.menu) this.closeMenu();
        else this.openMenu();
        return;
      }

      const option = event.target.closest('[data-category]');
      if (option) {
        this.setCategory(option.dataset.category);
        return;
      }

      const submitButton = event.target.closest('[data-submit]');
      if (submitButton) {
        if (!submitButton.disabled) this.submitDialog();
        return;
      }

      if (this.form.menu) this.closeMenu();
    });

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.form.menu) {
        event.preventDefault();
        this.closeMenu();
      }
    });

    this.dialog.addEventListener('input', (event) => {
      if (event.target.matches('[data-question]')) {
        this.form.question = event.target.value;
        this.syncDialog();
      }
    });

    this.dialog.addEventListener('close', () => {
      document.body.classList.remove('salut-qa-locked');
      if (this.dialogOpener && this.dialogOpener.isConnected) this.dialogOpener.focus();
      this.dialogOpener = null;
    });
  }

  openMenu() {
    this.form.menu = true;
    const select = this.dialog.querySelector('.salut-qa__form-select');
    const menu = this.dialog.querySelector('.salut-qa__form-menu');
    const trigger = this.dialog.querySelector('[data-menu-toggle]');
    if (select) select.classList.add('salut-qa__form-select--open');
    if (menu) menu.hidden = false;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  setCategory(value) {
    this.form.category = value;
    this.closeMenu();
    const label = this.dialog.querySelector('[data-menu-toggle] span');
    if (label) label.textContent = value;
    this.dialog.querySelectorAll('[data-category]').forEach((option) => {
      option.setAttribute('aria-selected', String(option.dataset.category === value));
    });
    const error = this.dialog.querySelector('[data-error-category]');
    if (error) error.hidden = true;
    this.syncDialog();
  }

  closeMenu() {
    this.form.menu = false;
    const select = this.dialog.querySelector('.salut-qa__form-select');
    const menu = this.dialog.querySelector('.salut-qa__form-menu');
    const trigger = this.dialog.querySelector('[data-menu-toggle]');
    if (select) select.classList.remove('salut-qa__form-select--open');
    if (menu) menu.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  submitDialog() {
    this.form.submitted = true;
    if (!this.form.category || !this.form.question.trim()) {
      this.renderDialog();
      return;
    }

    const submit = this.dialog.querySelector('[data-submit]');
    const previous = this.dialog.querySelector('[data-form-error]');
    if (previous) previous.remove();

    const fail = (message) => {
      if (submit) {
        submit.disabled = false;
        submit.classList.remove('salut-qa__form-submit--busy');
      }
      const notice = document.createElement('p');
      notice.className = 'salut-qa__form-error salut-qa__form-error--server';
      notice.setAttribute('data-form-error', '');
      notice.setAttribute('role', 'alert');
      notice.textContent = message || this.labels.form_error;
      if (submit) submit.before(notice);
    };

    const urls = this.endpoints();
    if (!urls) {
      fail();
      return;
    }

    if (submit) {
      submit.disabled = true;
      submit.classList.add('salut-qa__form-submit--busy');
    }

    const payload = new FormData();
    payload.set('product', this.dataset.product || '');
    payload.set('category', this.form.category);
    payload.set('question', this.form.question.trim());
    payload.set('locale', (document.documentElement.lang || '').trim());

    fetch(urls.submit, { method: 'POST', body: payload })
      .then((response) =>
        response
          .json()
          .catch(() => null)
          .then((data) => ({ ok: response.ok, status: response.status, data }))
      )
      .then(({ ok, status, data }) => {
        if (ok && data && data.ok) {
          this.renderThanks();
          return;
        }
        // Etichete traductibile, niciodata textul brut din data.errors.
        fail(status === 429 ? this.labels.form_error_rate : this.labels.form_error);
      })
      .catch(() => fail());
  }

  renderThanks() {
    this.dialog.innerHTML = `
      <div class="salut-qa__form salut-qa__form--thanks">
        <p class="salut-qa__form-thanks">${salutQaEscape(this.labels.form_thanks)}</p>
        <button class="salut-qa__form-submit salut-qa__form-submit--ready" type="button" data-close>${salutQaEscape(
          this.labels.form_close
        )}</button>
      </div>
    `;
  }

  syncDialog() {
    const serverError = this.dialog.querySelector('[data-form-error]');
    if (serverError) serverError.remove();
    const submit = this.dialog.querySelector('[data-submit]');
    if (!submit) return;
    const ready = Boolean(this.form.category) && Boolean(this.form.question.trim());
    submit.classList.toggle('salut-qa__form-submit--ready', ready);
    const error = this.dialog.querySelector('[data-error-question]');
    if (error) error.hidden = !this.form.submitted || Boolean(this.form.question.trim());
  }

  renderDialog() {
    const ready = Boolean(this.form.category) && Boolean(this.form.question.trim());
    const categoryError = this.form.submitted && !this.form.category;
    const questionError = this.form.submitted && !this.form.question.trim();

    this.dialog.innerHTML = `
      <div class="salut-qa__form" tabindex="-1">
        <div class="salut-qa__form-head">
          <h3 class="salut-qa__form-title">${salutQaEscape(this.labels.form_title)}</h3>
          <button class="salut-qa__form-close" type="button" data-close aria-label="${salutQaEscape(
            this.labels.form_cancel
          )}">${this.closeTemplate ? this.closeTemplate.innerHTML : ''}</button>
        </div>
        <div class="salut-qa__form-body">
          <div class="salut-qa__form-field">
            <div class="salut-qa__form-select${this.form.menu ? ' salut-qa__form-select--open' : ''}">
              <button
                class="salut-qa__form-trigger"
                type="button"
                data-menu-toggle
                aria-expanded="${this.form.menu}"
                aria-haspopup="listbox"
              >
                <span>${salutQaEscape(this.form.category || this.labels.form_category)}</span>
                <span class="salut-qa__form-chevron">${
                  this.chevronTemplate ? this.chevronTemplate.innerHTML : ''
                }</span>
              </button>
              <div class="salut-qa__form-menu" role="listbox" ${this.form.menu ? '' : 'hidden'}>
                ${this.categories()
                  .map(
                    (item) =>
                      `<button class="salut-qa__form-option" type="button" role="option" aria-selected="${
                        item === this.form.category
                      }" data-category="${salutQaEscape(item)}">${salutQaEscape(item)}</button>`
                  )
                  .join('')}
              </div>
            </div>
            <span class="salut-qa__form-error" data-error-category ${categoryError ? '' : 'hidden'}>${salutQaEscape(
      this.labels.form_error_category
    )}</span>
          </div>
          <div class="salut-qa__form-field">
            <textarea
              class="salut-qa__form-question"
              data-question
              rows="2"
              placeholder="${salutQaEscape(this.labels.form_question)}"
              aria-label="${salutQaEscape(this.labels.form_question)}"
            >${salutQaEscape(this.form.question)}</textarea>
            <span class="salut-qa__form-error" data-error-question ${
              questionError ? '' : 'hidden'
            }>${salutQaEscape(this.labels.form_error_question)}</span>
          </div>
        </div>
        <button class="salut-qa__form-submit${
          ready ? ' salut-qa__form-submit--ready' : ''
        }" type="button" data-submit>${salutQaEscape(this.labels.form_submit)}</button>
      </div>
    `;
  }
}

if (!customElements.get('salut-qa')) {
  customElements.define('salut-qa', SalutQa);
}
